import {
  useCallback, useContext, useMemo, useRef,
} from 'react';
import {
  selectCurrentSelectedChat,
  getAllMessagesByChatId,
  selectMetadataById,
  updateMetadata,
  TwilioMessage,
  selectUnreadCountById,
  ConversationType,
  setChatAlert,
  MessageDirection,
  MessageAuthorGroup,
  MessageDisplay,
  DateMessageMap,
  addMessages,
  Paginator,
  Message,
} from 'models/twilio';
import { batch, useDispatch, useSelector } from 'react-redux';
import clamp from 'lodash/clamp';
import last from 'lodash/last';
import throttle from 'lodash/throttle';
import moment from 'moment';
import { BEHAVIOR } from '../constants';
import { removeUnreadIndicator } from '../utils/renderUnreadIndicator';
import useCurrentAttendee from './useCurrentAttendee';
import useFirebaseChatConfig from './useFirebaseChatConfig';
import useMutableRef from './useMutableRef';
import ConverastionContext from '../ConversationContext';

export enum TwilioGetMessageSrcCall {
  GENERAL = 'GENERAL',
  PREV = 'PREV',
  NEXT = 'NEXT',
}

const useTwilioChat = () => {
  const dispatch = useDispatch();
  const { isCurrentAttendee } = useCurrentAttendee();
  const { hideChatMillis } = useFirebaseChatConfig();
  const messageListRef = useRef<HTMLDivElement>();
  const currentSelectedChat = useSelector(selectCurrentSelectedChat);
  const metadataRef = useMutableRef(useSelector(selectMetadataById(currentSelectedChat)));
  const { getConversationById } = useContext(ConverastionContext);
  const conversation = getConversationById(currentSelectedChat);
  const messages = useSelector(getAllMessagesByChatId(currentSelectedChat));
  const unreadCount = useSelector(selectUnreadCountById(currentSelectedChat));

  const determineDirection = useCallback((author: string) => (isCurrentAttendee(author)
    ? MessageDirection.OUTGOING
    : MessageDirection.INCOMING
  ), [isCurrentAttendee]);

  const getMessageHandler = useCallback(async (
    getFn: () => Promise<Paginator<Message>>,
    sid: string,
    sourceCall: TwilioGetMessageSrcCall = TwilioGetMessageSrcCall.GENERAL,
  ) => {
    const {
      items,
      hasNextPage,
      hasPrevPage,
      nextPage,
      prevPage,
    } = await getFn();
    const updates = {
      id: sid,
      prevPage,
      nextPage,
      hasNextPage,
      hasPrevPage,
    } as Parameters<typeof updateMetadata>[0];
    if (sourceCall === TwilioGetMessageSrcCall.PREV) delete updates.hasNextPage;
    if (sourceCall === TwilioGetMessageSrcCall.NEXT) delete updates.hasPrevPage;
    if (sourceCall !== TwilioGetMessageSrcCall.PREV && items.length) {
      updates.lastFetchedMessageIndex = items[items.length - 1].index;
    }
    batch(() => {
      dispatch(addMessages({ conversationId: sid, messages: items }));
      dispatch(updateMetadata(updates));
    });
  }, [dispatch]);

  const generateMessageAuthorGroup = useCallback((message: TwilioMessage): MessageAuthorGroup => ({
    author: message.author,
    direction: determineDirection(message.author),
    renderSender: [ConversationType.GROUP, ConversationType.PUBLIC].includes(metadataRef.current.conversationType),
    messages: [message],
    startMessageIndex: message.index,
  }), [determineDirection, metadataRef]);

  const atBottom = useCallback(() => {
    const $chat = messageListRef.current;
    if (!$chat) return false;
    return $chat.offsetHeight === $chat.scrollHeight
      || $chat.scrollTop + $chat.offsetHeight >= ($chat.scrollHeight - BEHAVIOR.FETCH_MORE_MARGIN);
  }, [messageListRef]);

  const fetchUpToIndex = useCallback(async (targetIndex: number) => {
    const { MAX_FETCH, FETCH_PAGE_SIZE } = BEHAVIOR;
    const currentLastIndex = conversation.lastMessage?.index || 0;
    const numToFetch = Math.min(currentLastIndex - targetIndex, MAX_FETCH);
    const numFetches = clamp(Math.ceil(numToFetch / FETCH_PAGE_SIZE), 1, Math.floor(MAX_FETCH / FETCH_PAGE_SIZE));
    const fetchPromises = Array.from({ length: numFetches - 1 }).map((_x, i) => i)
      .map((fetchNumber) => getMessageHandler(
        () => conversation.getMessages(FETCH_PAGE_SIZE, currentLastIndex - fetchNumber * FETCH_PAGE_SIZE),
        conversation.sid,
        TwilioGetMessageSrcCall.PREV,
      ));
    await Promise.all(fetchPromises);
    await getMessageHandler(
      () => conversation.getMessages(FETCH_PAGE_SIZE, currentLastIndex - (numFetches - 1) * FETCH_PAGE_SIZE),
      conversation.sid,
      TwilioGetMessageSrcCall.PREV,
    );
  }, [conversation, getMessageHandler]);

  const initialMessageFetch = useCallback(async () => {
    try {
      if (!conversation || !metadataRef.current) return;
      const { lastReadMessageIndex, lastMessage } = metadataRef.current;
      if (!lastMessage || lastReadMessageIndex < 0) {
        await getMessageHandler(
          () => conversation.getMessages(BEHAVIOR.FETCH_PAGE_SIZE),
          conversation.sid,
        );
      } else {
        await fetchUpToIndex(lastReadMessageIndex);
      }
      dispatch(updateMetadata({
        id: conversation.sid,
        hasLoadedMessages: true,
      }));
    } catch (e) {
      dispatch(setChatAlert({ message: e.message, severity: 'error' }));
    }
  }, [dispatch, getMessageHandler, conversation, metadataRef, fetchUpToIndex]);

  const fetchNextMessages = useCallback(async () => {
    const $chat = messageListRef.current;
    if (!metadataRef.current || !$chat) return;
    const { hasNextPage, nextPage } = metadataRef.current;
    if (hasNextPage) await getMessageHandler(nextPage, metadataRef.current.id, TwilioGetMessageSrcCall.NEXT);
  }, [getMessageHandler, metadataRef, messageListRef]);

  const fetchPrevMessages = useCallback(async () => {
    const $chat = messageListRef.current;
    if (!metadataRef.current || !$chat) return;
    const { hasPrevPage, prevPage } = metadataRef.current;
    if (hasPrevPage) {
      const preFetchBottom = $chat.scrollHeight - $chat.scrollTop;
      await getMessageHandler(prevPage, metadataRef.current.id, TwilioGetMessageSrcCall.PREV);
      $chat.scrollTop = $chat.scrollHeight - preFetchBottom;
    }
  }, [getMessageHandler, metadataRef, messageListRef]);

  const setAllMessagesConsumed = useCallback(async () => {
    if (!conversation || !metadataRef.current) return;
    removeUnreadIndicator();
    const lastMessageIndex = metadataRef.current.lastMessage?.index ?? -1;
    if (lastMessageIndex > metadataRef.current.lastReadMessageIndex) await conversation.setAllMessagesRead();
  }, [conversation, metadataRef]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScrollHandler = useCallback(throttle(() => {
    const $chat = messageListRef.current;
    if (!metadataRef.current || !$chat) return;
    if ($chat.scrollTop < BEHAVIOR.FETCH_MORE_MARGIN) fetchPrevMessages();
    else if (atBottom() && metadataRef.current.hasNextPage) fetchNextMessages();
    else if (atBottom() && unreadCount > 0) setAllMessagesConsumed();
  }, 200), [
    messageListRef,
    metadataRef,
    fetchPrevMessages,
    fetchNextMessages,
    setAllMessagesConsumed,
    unreadCount,
  ]);

  // Group messages by: Date => Author in O(2n)
  const displayGroup = useMemo(() => {
    if (!messages.length || !metadataRef.current?.hasLoadedMessages) return [];
    const filteredMesages = hideChatMillis
      ? messages.filter((message) => message.dateCreated.getTime() > hideChatMillis)
      : messages;
    // 1. Group by date
    const dateMessageMap = filteredMesages.reduce((dateMap, message) => {
      const createDate = moment(message.dateCreated).tz(BEHAVIOR.TIME_ZONE).format(BEHAVIOR.DATE_FORMAT);
      if (createDate in dateMap) dateMap[createDate].push(message);
      else dateMap[createDate] = [message];
      return dateMap;
    }, {} as DateMessageMap);

    // 2. Within each date, group by author
    const dateMessageAuthorGroup = Object.entries(dateMessageMap).reduce((acc, [dateString, groupedMessages]) => {
      // Create MessageAuthorGroup from groupMessages
      const groupInit = [generateMessageAuthorGroup(groupedMessages[0])];
      const messageAuthorGroup = groupedMessages.slice(1).reduce((arrayGroups, message) => {
        const lastGroup = last(arrayGroups);
        if (lastGroup.author === message.author) arrayGroups[arrayGroups.length - 1].messages.push(message);
        else arrayGroups.push(generateMessageAuthorGroup(message));
        return arrayGroups;
      }, groupInit);
      acc.push({ dateString, messageGroups: messageAuthorGroup });
      return acc;
    }, [] as MessageDisplay);

    return dateMessageAuthorGroup;
  }, [metadataRef, messages, generateMessageAuthorGroup, hideChatMillis]);

  return useMemo(() => ({
    messageListRef,
    onScrollHandler,
    displayGroup,
    initialMessageFetch,
    setAllMessagesConsumed,
  }), [
    messageListRef,
    onScrollHandler,
    displayGroup,
    initialMessageFetch,
    setAllMessagesConsumed,
  ]);
};

export default useTwilioChat;
